import React, { useState, useEffect } from 'react';
import { dashboardAPI } from '../../services/api';
import { formatCurrency, formatDate, getStatusColor } from '../../utils/helpers';
import Spinner from '../../components/common/Spinner';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const StatCard = ({ label, value, icon, color }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-6 flex items-center gap-4">
    <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center text-2xl`}>{icon}</div>
    <div>
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  </div>
);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dashboardAPI.getStats()
      .then(res => setStats(res.data.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center items-center h-64"><Spinner size="lg" /></div>;
  if (!stats) return <p className="text-gray-500">Failed to load stats.</p>;

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const chartData = {
    labels: stats.revenueByMonth.map(r => `${monthNames[r._id.month - 1]} ${r._id.year}`),
    datasets: [{
      label: 'Revenue',
      data: stats.revenueByMonth.map(r => r.revenue),
      backgroundColor: 'rgba(79, 70, 229, 0.8)',
      borderRadius: 6
    }]
  };

  const statCards = [
    { label: 'Total Customers', value: stats.totalCustomers, icon: '🏢', color: 'bg-blue-100' },
    { label: 'Active Subscriptions', value: stats.activeSubscriptions, icon: '✅', color: 'bg-green-100' },
    { label: 'Expired Subscriptions', value: stats.expiredSubscriptions, icon: '⏰', color: 'bg-orange-100' },
    { label: 'Total Revenue', value: formatCurrency(stats.totalRevenue), icon: '💰', color: 'bg-yellow-100' },
    { label: 'MRR', value: formatCurrency(stats.mrr), icon: '📈', color: 'bg-purple-100' },
    { label: 'Failed Payments', value: stats.failedPayments, icon: '❌', color: 'bg-red-100' },
    { label: 'Trial Users', value: stats.trialUsers, icon: '🆓', color: 'bg-indigo-100' }
  ];

  return (
    <div className="space-y-6">
      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map(card => <StatCard key={card.label} {...card} />)}
      </div>

      {/* Chart */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Revenue Overview (Last 6 Months)</h2>
        {stats.revenueByMonth.length > 0 ? (
          <Bar data={chartData} options={{ responsive: true, plugins: { legend: { display: false } } }} />
        ) : (
          <p className="text-gray-400 text-center py-8">No revenue data yet</p>
        )}
      </div>

      {/* Recent Customers */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Customers</h2>
        {stats.recentCustomers.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="pb-3 font-medium">Name</th>
                <th className="pb-3 font-medium">Email</th>
                <th className="pb-3 font-medium">Status</th>
                <th className="pb-3 font-medium">Plan</th>
                <th className="pb-3 font-medium">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {stats.recentCustomers.map(c => (
                <tr key={c._id}>
                  <td className="py-3 font-medium">{c.name}</td>
                  <td className="py-3 text-gray-500">{c.email}</td>
                  <td className="py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(c.status)}`}>{c.status}</span>
                  </td>
                  <td className="py-3 text-gray-500">{c.currentSubscription?.plan?.name || '—'}</td>
                  <td className="py-3 text-gray-500">{formatDate(c.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-gray-400 text-center py-8">No customers yet</p>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
